import React, { useState } from "react";
import Table from "rc-table";
import moment from "moment";
import { Button, Pagination } from "../../shared";
import { useGetOrders } from "../../hooks/order";
import axiosInstance from "../../axiosConfig";
import {
  AllegroOrder,
  SubiektReservation,
} from "../../types/AllegroOrdersTypes";
import "./OrdersPage.scss";

type OrdersPageProps = {};

const STATUS_LABELS: { [key: string]: { label: string; className: string } } =
  {
    BOUGHT: { label: "Kupione", className: "badge--pending" },
    FILLED_IN: { label: "Uzupełnione", className: "badge--pending" },
    READY_FOR_PROCESSING: { label: "Do realizacji", className: "badge--ready" },
    CANCELLED: { label: "Anulowane", className: "badge--cancelled" },
    BUYER_CANCELLED: { label: "Anulowane", className: "badge--cancelled" },
    AUTO_CANCELLED: { label: "Anulowane", className: "badge--cancelled" },
  };

const RESERVATION_LABELS: {
  [key: string]: { label: string; className: string };
} = {
  pending: { label: "W trakcie", className: "badge--pending" },
  created: { label: "Zarezerwowano", className: "badge--ready" },
  failed: { label: "Błąd rezerwacji", className: "badge--cancelled" },
  cancelled: { label: "Zwolniona", className: "badge--neutral" },
  cancel_failed: { label: "Błąd zwalniania", className: "badge--cancelled" },
};

// How Subiekt covered the order when the document was created
const RESERVATION_RESULTS: { [key: string]: string } = {
  Zarezerwowane: "całość ze stanu",
  CzesciowoZarezerwowane: "część ze stanu, reszta do zamówienia",
  BrakNaStanie: "brak na stanie, całość do zamówienia",
};

const OrdersPage: React.FC<OrdersPageProps> = () => {
  const [page, setPage] = useState(1);
  const [isSyncing, setIsSyncing] = useState(false);
  const { data, isLoading, refetch } = useGetOrders(page);

  const handleSync = async () => {
    try {
      setIsSyncing(true);
      await axiosInstance.get("/allegro/orders?limit=100");
      await refetch();
    } catch (error: any) {
      console.error("Error fetching orders from Allegro:", error);
    } finally {
      setIsSyncing(false);
    }
  };

  const formatDate = (date: string | null) =>
    date ? moment(date).local().format("DD.MM.YYYY, HH:mm") : "-";

  const formatPrice = (amount: number | null, currency: string | null) => {
    if (amount === undefined || amount === null) {
      return "-";
    }
    return `${Number(amount).toFixed(2)} ${
      !currency || currency === "PLN" ? "zł" : currency
    }`;
  };

  const renderStatus = (record: AllegroOrder) => {
    const status = STATUS_LABELS[record.status];
    return (
      <span className={`badge ${status?.className || ""}`}>
        {status?.label || record.status}
      </span>
    );
  };

  const renderReservation = (reservation: SubiektReservation) => {
    const status = reservation.releaseRequired
      ? { label: "Do zwolnienia", className: "badge--cancelled" }
      : RESERVATION_LABELS[reservation.status];
    const hint = reservation.releaseRequired
      ? "Zamówienie anulowane - usuń dokument w Subiekcie"
      : reservation.message ||
        (reservation.status === "created" && reservation.resultStatus
          ? RESERVATION_RESULTS[reservation.resultStatus]
          : null);

    return (
      <div key={reservation.id} className="order-reservation">
        <span className={`badge ${status?.className || ""}`}>
          {status?.label || reservation.status}
        </span>
        {reservation.number && (
          <div className="order-reservation__number">{reservation.number}</div>
        )}
        {hint && <div className="order-reservation__hint">{hint}</div>}
      </div>
    );
  };

  const renderReservations = (record: AllegroOrder) =>
    record.reservations?.length
      ? record.reservations.map((reservation) => renderReservation(reservation))
      : "-";

  const renderItems = (record: AllegroOrder) => (
    <div className="order-items">
      {record.items.map((item) => (
        <div key={item.id} className="order-items__item">
          {item.quantity} × {item.offerName || item.offerId}
        </div>
      ))}
    </div>
  );

  const renderBuyer = (record: AllegroOrder) => (
    <div className="order-buyer">
      <div>{record.buyerName || record.buyerLogin || "-"}</div>
      {record.buyerEmail && (
        <div className="order-buyer__email">{record.buyerEmail}</div>
      )}
    </div>
  );

  const columns = [
    {
      title: "DATA ZAKUPU",
      key: "orderedAt",
      render: (_: any, record: AllegroOrder) => formatDate(record.orderedAt),
    },
    {
      title: "KUPUJĄCY",
      key: "buyer",
      render: (_: any, record: AllegroOrder) => renderBuyer(record),
    },
    {
      title: "PRODUKTY",
      key: "items",
      render: (_: any, record: AllegroOrder) => renderItems(record),
    },
    {
      title: "DOSTAWA",
      key: "deliveryMethodName",
      render: (_: any, record: AllegroOrder) =>
        record.deliveryMethodName || "-",
    },
    {
      title: "DO ZAPŁATY",
      key: "totalToPay",
      render: (_: any, record: AllegroOrder) => (
        <span className="order-price">
          {formatPrice(record.totalToPay, record.currency)}
        </span>
      ),
    },
    {
      title: "REZERWACJA",
      key: "reservations",
      render: (_: any, record: AllegroOrder) => renderReservations(record),
    },
    {
      title: "STATUS",
      key: "status",
      render: (_: any, record: AllegroOrder) => renderStatus(record),
    },
  ];

  const mobileColumns = [
    {
      title: "Zamówienia",
      key: "mobile",
      render: (_: any, record: AllegroOrder) => (
        <div className="mobile-record">
          <div className="mobile-record__date">
            {formatDate(record.orderedAt)}
          </div>
          <div className="mobile-record__user">
            {record.buyerName || record.buyerLogin || "-"} ·{" "}
            {formatPrice(record.totalToPay, record.currency)}
          </div>
          {renderItems(record)}
          {renderStatus(record)}
          {record.reservations?.length > 0 && renderReservations(record)}
        </div>
      ),
    },
  ];

  if (isLoading) {
    return <div className="loading">Ładowanie...</div>;
  }

  return (
    <div className="prices-page">
      <div className="prices-page__header">
        <h1>Zamówienia</h1>
        <Button
          label={isSyncing ? "Pobieranie..." : "Pobierz z Allegro"}
          onClick={handleSync}
          styleType="primary"
          disabled={isSyncing}
        />
      </div>

      <Table
        rowKey="id"
        className="prices-table orders-table desktop-table"
        data={data?.data || []}
        columns={columns}
        emptyText="Brak zamówień do wyświetlenia"
      />

      <Table
        rowKey="id"
        className="prices-table orders-table mobile-table"
        data={data?.data || []}
        columns={mobileColumns}
        showHeader={false}
        emptyText="Brak zamówień do wyświetlenia"
      />

      {data?.meta && data?.meta?.lastPage > 1 && (
        <Pagination
          totalPages={data?.meta?.lastPage}
          currentPage={data?.meta?.currentPage || page}
          onPageChange={(newPage: number) => setPage(newPage)}
        />
      )}
    </div>
  );
};

export default OrdersPage;
